"use client";

/**
 * A `multiselect` question drawn as a closed dropdown with checkboxes, for questions whose choice
 * list is too long to show open (e.g. a list of cities). Chosen per question with
 * `FormField.display_as_dropdown`.
 *
 * Only the control differs from the checkbox list: the answer is the same array of choices, with
 * "Other" stored as `{ value: "__OTHER__", otherText }`, so responses, exports, reveal logic and
 * prefill treat both displays identically.
 */

import { FetchInput } from "@/fetch-ui/FetchInput";
import { FetchMultiselect } from "@/fetch-ui/FetchMultiselect";
import { MULTISELECT_DROPDOWN_SEARCH_MIN_CHOICES } from "@/features/forms/_logic/constants/field-type-constants";
import { OTHER_OPTION_LABEL, OTHER_OPTION_VALUE } from "@/features/forms/_logic/field-utils";
import type { FormField } from "@/lib/database/forms";

/** One stored multi-select answer item: a plain choice, or the "Other" object. */
type MultiSelectItem = string | { value: string; otherText?: string };

interface MultiSelectDropdownFieldProps {
  field: FormField;
  value: unknown;
  onChange?: (value: MultiSelectItem[]) => void;
  disabled?: boolean;
}

/**
 * Remove markdown link syntax from a choice, keeping the link text. A dropdown row can't hold a link.
 *
 * @param text - The choice as written by the admin.
 * @returns The choice with `[text](url)` replaced by `text`.
 */
function stripMarkdownLinks(text: string): string {
  return text.replace(/\[([^\]]+)\]\s*\(\s*https?:\/\/[^)\s]+\s*\)/g, "$1");
}

/**
 * Whether a stored item is the "Other" choice.
 *
 * @param item - One stored answer item.
 * @returns True for the bare sentinel or the `{ value: "__OTHER__" }` object.
 */
function isOtherItem(item: MultiSelectItem): boolean {
  return item === OTHER_OPTION_VALUE || (typeof item === "object" && item?.value === OTHER_OPTION_VALUE);
}

/**
 * Render a multi-select question as a dropdown.
 *
 * @param props.field - The question; reads `choices` and `has_other_option`.
 * @param props.value - The current answer; anything but an array is treated as no selection.
 * @param props.onChange - Called with the full new answer array.
 * @param props.disabled - Renders the control inert (builder previews).
 * @returns The dropdown, plus the "Please specify" box while "Other" is selected.
 */
export function MultiSelectDropdownField({ field, value, onChange, disabled = false }: MultiSelectDropdownFieldProps) {
  // Derived values
  const currentItems: MultiSelectItem[] = Array.isArray(value) ? value : [];
  const otherItem = currentItems.find(isOtherItem);
  const otherText = typeof otherItem === "object" ? (otherItem.otherText ?? "") : "";
  const selectedValues = currentItems.map((item) => (isOtherItem(item) ? OTHER_OPTION_VALUE : (item as string)));

  const choices = field.choices ?? [];
  const options = [
    ...choices.map((choice) => ({ value: choice, label: stripMarkdownLinks(choice) })),
    ...(field.has_other_option ? [{ value: OTHER_OPTION_VALUE, label: OTHER_OPTION_LABEL }] : []),
  ];

  // Handlers
  const handleSelectionChange = (nextValues: string[]) => {
    if (!onChange || disabled) return;
    // Keep any "Other" text already typed when another choice is toggled.
    onChange(nextValues.map((next) => (next === OTHER_OPTION_VALUE ? { value: OTHER_OPTION_VALUE, otherText } : next)));
  };

  const handleOtherTextChange = (text: string) => {
    onChange?.(currentItems.map((item) => (isOtherItem(item) ? { value: OTHER_OPTION_VALUE, otherText: text } : item)));
  };

  // Render
  return (
    <div className="flex flex-col gap-3">
      <FetchMultiselect
        options={options}
        selectedValues={selectedValues}
        onChange={handleSelectionChange}
        placeholder="Select options"
        searchable={options.length >= MULTISELECT_DROPDOWN_SEARCH_MIN_CHOICES}
        disabled={disabled}
        ariaLabel={stripMarkdownLinks(field.label)}
      />

      {otherItem !== undefined && !disabled && (
        <div className="flex flex-col gap-1 border-l-2 border-gray-300 pl-4">
          <label className="text-sm font-medium text-gray-600">Please specify:</label>
          <FetchInput
            type="text"
            value={otherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder="Type your answer..."
          />
        </div>
      )}
    </div>
  );
}
